import { Box, useMediaQuery, useTheme } from '@mui/material';
import { formatTranslation } from 'intl';
import { AnalyticsTranslationKeys, InspectionTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import {
  CHART_TRIPLET_GAUGE_SIZE,
  CHART_TRIPLET_LAYOUT_HEIGHT,
  CHART_TRIPLET_LAYOUT_MAX_WIDTH,
  CHART_TRIPLET_LAYOUT_MAX_WIDTH_VERTICAL,
  CHART_TRIPLET_MOBILE_THRESHOLD,
} from '../tokens/muiChartLayout';
import { getChartAreaSx } from '../theme/muiChartSlotProps';
import type { ReactElement } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import {
  TripletCountDisplay,
  TripletGaugeDisplay,
  TRIPLET_GAUGE_COLORS,
} from './triplet/tripletDisplay';
import { toInspectionSummarySeries } from './triplet/inspectionSummaryParts';

type TripletChartViewProps = Readonly<{
  chartId: string;
  values: readonly (number | undefined)[];
}>;

const labelSx = {
  fontSize: '16px',
  textAlign: 'center',
} satisfies SxProps<Theme>;

const tripletLayoutCenterSx = {
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
} satisfies SxProps<Theme>;

function HorizontalTripletLayout({
  chartId,
  values,
  labels,
}: Readonly<{
  chartId: string;
  values: readonly (number | undefined)[];
  labels: readonly [string, string, string];
}>): ReactElement {
  const { inspectionCount, failedFactor, inspectionResult } =
    toInspectionSummarySeries(values);
  const [inspectionCountLabel, failedFactorLabel, inspectionResultLabel] =
    labels;

  return (
    <Box sx={tripletLayoutCenterSx}>
      <Box
        sx={{
          width: '100%',
          maxWidth: CHART_TRIPLET_LAYOUT_MAX_WIDTH,
          height: CHART_TRIPLET_LAYOUT_HEIGHT,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          gridTemplateRows: '2fr 1fr',
        }}
      >
        <TripletCountDisplay
          chartId={chartId}
          value={inspectionCount}
          label={inspectionCountLabel}
          size={CHART_TRIPLET_GAUGE_SIZE}
          variant='details'
        />
        <TripletGaugeDisplay
          chartId={chartId}
          gaugeIndex={0}
          value={failedFactor}
          color={TRIPLET_GAUGE_COLORS.failedFactor}
          label={failedFactorLabel}
        />
        <TripletGaugeDisplay
          chartId={chartId}
          gaugeIndex={1}
          value={inspectionResult}
          color={TRIPLET_GAUGE_COLORS.result}
          label={inspectionResultLabel}
        />
        <Box sx={labelSx}>{inspectionCountLabel}</Box>
        <Box sx={labelSx}>{failedFactorLabel}</Box>
        <Box sx={labelSx}>{inspectionResultLabel}</Box>
      </Box>
    </Box>
  );
}

function VerticalTripletLayout({
  chartId,
  values,
  labels,
}: Readonly<{
  chartId: string;
  values: readonly (number | undefined)[];
  labels: readonly [string, string, string];
}>): ReactElement {
  const { inspectionCount, failedFactor, inspectionResult } =
    toInspectionSummarySeries(values);
  const [inspectionCountLabel, failedFactorLabel, inspectionResultLabel] =
    labels;

  return (
    <Box sx={tripletLayoutCenterSx}>
      <Box
        sx={{
          width: '100%',
          maxWidth: CHART_TRIPLET_LAYOUT_MAX_WIDTH_VERTICAL,
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'repeat(3, 2fr 0.5fr)',
        }}
      >
        <TripletCountDisplay
          chartId={chartId}
          value={inspectionCount}
          label={inspectionCountLabel}
          size={CHART_TRIPLET_GAUGE_SIZE}
          variant='details'
        />
        <Box sx={labelSx}>{inspectionCountLabel}</Box>
        <TripletGaugeDisplay
          chartId={chartId}
          gaugeIndex={0}
          value={failedFactor}
          color={TRIPLET_GAUGE_COLORS.failedFactor}
          label={failedFactorLabel}
        />
        <Box sx={labelSx}>{failedFactorLabel}</Box>
        <TripletGaugeDisplay
          chartId={chartId}
          gaugeIndex={1}
          value={inspectionResult}
          color={TRIPLET_GAUGE_COLORS.result}
          label={inspectionResultLabel}
        />
        <Box sx={labelSx}>{inspectionResultLabel}</Box>
      </Box>
    </Box>
  );
}

export function TripletChartView({
  chartId,
  values,
}: TripletChartViewProps): ReactElement {
  const intl = useIntl();
  const theme = useTheme();
  const isVertical = useMediaQuery(
    theme.breakpoints.down(CHART_TRIPLET_MOBILE_THRESHOLD)
  );

  const labels = [
    formatTranslation(intl, AnalyticsTranslationKeys.inspectionsChart),
    formatTranslation(
      intl,
      InspectionTranslationKeys.failedInspectionFactorChart
    ),
    formatTranslation(intl, AnalyticsTranslationKeys.inspectionResultChart),
  ] as const;

  const layoutProps = { chartId, values, labels };

  return (
    <Box sx={{ ...getChartAreaSx(), height: '100%', width: '100%' }}>
      {isVertical ? (
        <VerticalTripletLayout {...layoutProps} />
      ) : (
        <HorizontalTripletLayout {...layoutProps} />
      )}
    </Box>
  );
}
